import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Paper, useTheme, TextField, Typography, ThemeProvider, CssBaseline, Backdrop, CircularProgress } from "@mui/material";
import { Add, Cancel, Delete, Edit, PlayArrow, Publish, Save, Visibility } from "@mui/icons-material";
import { CREATE_TESTSUITE_URL, DELETE_TESTSUITE_URL, GET_ALL_VERSIONS_TESTSUITES_URL, UPDATE_TESTSUITE_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { TestSuite } from "../../Interfaces/AutomateTest";
import { useSelector } from "react-redux";
import { RootState } from "state/store";


export const TestSuiteVersions: React.FC = () => {
   const { tenantId, testSuiteId } = useParams();
   const [testSuitesVersions, setTestSuitesVersions] = useState<TestSuite[]>([]);
   const [editMode, setEditMode] = useState<string | null>(null);
   const [testSuiteVersionEditedRow, setTestSuiteVersionEditedRow] = useState<Partial<TestSuite>>({});
   const [newTestSuiteVersionRow, setnewTestSuiteVersionRow] = useState<TestSuite | null>(null);
   const navigate = useNavigate();
   const theme = useTheme();
   const [loading, setLoading] = useState<boolean>(false);
   const user = useSelector((state: RootState) => state.authentication.user);

// Fetch All versions of TestSuite.

    const fetchTestSuiteVersions = async () => {
      try {
        setLoading(true);
        const response = await fetch(GET_ALL_VERSIONS_TESTSUITES_URL+`/${tenantId}/${testSuiteId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
  
        const data = await response.json();
        setTestSuitesVersions(data.data);
        setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersion.ts : fetchTestSuiteVersions(): ","Result is:"+ JSON.stringify(data.data, null, 2));
      } catch (error) {
          setTestSuitesVersions([]);
          setLoading(false);
          Logger.error("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersion.ts : fetchTestSuiteVersions(): ", "Failed to fetch testSuiteVersions:"+ error);
      }
    };
    useEffect(() => {
        fetchTestSuiteVersions();
    }, []);

  const handleAddNewTestSuiteVersionRow = () => {
    const maxVersion = testSuitesVersions
    .filter((testSuite) => testSuite.testSuiteId === testSuiteId)
    .reduce((max, testSuite) => Math.max(max, testSuite.versionNumber), 1);

    const newTestSuiteVersion: TestSuite = {
      _id: "",
      testSuiteId: testSuiteId as string,
      name: "testSuite",
      userId: user._id,
      companyId: user.companyId,
      versionNumber: parseFloat((maxVersion + 0.1).toFixed(1)),
      tenantId: tenantId as string,
      description: "",
      status: "",
      testCases: [],
      dataSets: [],
    };
    setnewTestSuiteVersionRow(newTestSuiteVersion);
  };

  const handleInputChange = (versionNumber: number, field: keyof TestSuite, value: string) => {
    if (versionNumber === -1) {
      // Update the newTestSuiteVersionRow state
      setnewTestSuiteVersionRow((prev) => prev ? { ...prev, [field]: value } : null);
    } else if (editMode) {
      // Update the testSuiteVersionEditedRow state
      setTestSuiteVersionEditedRow((prev) => ({ ...prev, [field]: value }));
    } else {
      // Update the testSuites array for existing rows
      setTestSuitesVersions((prev) =>
        prev.map((testSuite) =>
          testSuite.versionNumber === versionNumber ? { ...testSuite, [field]: value } : testSuite
        )
      );
    }
  };

  const handleCreateNewTestSuiteVersion = async () => {
    if (newTestSuiteVersionRow) {
      try {
        setLoading(true);
        const response = await fetch(CREATE_TESTSUITE_URL+`/${tenantId}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newTestSuiteVersionRow),
        });

        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTestSuiteVersions();
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersions.ts : handleCreateNewTestSuiteVersion(): ", "TestSuiteVersion created Successfully.");
        setnewTestSuiteVersionRow(null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersions.ts : handleCreateNewTestSuiteVersion(): ", "Failed to save new TestSuite: "+ error);
      }
    }
  };

  const handleUpdateTestSuiteVersion = async (_id:string) => {
    try {
      setLoading(true);
      const response = await fetch(`${UPDATE_TESTSUITE_URL}/${_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testSuiteVersionEditedRow),
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }

      setTestSuitesVersions((prev) =>
        prev.map((testSuite) =>
            testSuite.versionNumber === testSuiteVersionEditedRow.versionNumber ? { ...testSuite, ...testSuiteVersionEditedRow } : testSuite
        )
      );
      setEditMode(null);
      setTestSuiteVersionEditedRow({});
      setLoading(false);
    } catch (error) {
      setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersions.ts : handleUpdateTestSuiteVersion(): ", "Failed to save testSuite:" + error);
    }
  };

  const handleCancelEdit = () => {
    setnewTestSuiteVersionRow(null);
    setEditMode(null);
    setTestSuiteVersionEditedRow({});
  };

  const handleTestSuiteVersionEdit = (_id: string) => {
    setEditMode(_id);
    const testSuite = testSuitesVersions.find((testSuite) => testSuite._id === _id);
    if (testSuite) setTestSuiteVersionEditedRow(testSuite);
  };

  const handlePublishTestSuiteVersion = async (_id: string) => {
    try {
      const updatedTestSuiteVersion = testSuitesVersions.find(
        (testSuiteVersion) => testSuiteVersion._id === _id
      );
  
      if (!updatedTestSuiteVersion) return;
  
      const payload = { ...updatedTestSuiteVersion, status: "Published" };
      setLoading(true);
      const response = await fetch(`${UPDATE_TESTSUITE_URL}/${_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }
  
      await fetchTestSuiteVersions();
      setLoading(false);
      Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersions.ts : handlePublishTestSuiteVersion(): ", `TestSuiteVersion ${_id} published successfully.`);
    } catch (error) {
      setLoading(false);
      Logger.error("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersions.ts : handlePublishTestSuiteVersion(): ", "Failed to publish testSuite: " + error);
    }
  };
  

  const handleDeleteTestSuiteVersion = async(_id: string) => {
    try {
      setLoading(true);
        const response = await fetch(DELETE_TESTSUITE_URL+`/${_id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTestSuiteVersions();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/TestSuiteVersions/TestSuiteVersions.ts : handleDeleteTestSuiteVersion(): ", "Failed to delete testSuite:"+ error);
      }
  };

  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h4" gutterBottom>
        TestSuit Versions
      </Typography>
      <Button variant="contained" sx={{ backgroundColor: theme.palette.primary.main }} startIcon={<Add />} onClick={handleAddNewTestSuiteVersionRow}>
        New Version
      </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>VERSION</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell>TEST CASES</TableCell>
              <TableCell sx={{display: "flex", justifyContent: "flex-end"}}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testSuitesVersions.map((testSuite, index) => (
              <TableRow key={index}>
                <TableCell>{testSuite.versionNumber}</TableCell>
                <TableCell>
                  {editMode === testSuite._id ? (
                    <TextField
                      value={testSuiteVersionEditedRow.name || ""}
                      onChange={(e) => handleInputChange(testSuite.versionNumber, "name", e.target.value)}
                    />
                  ) : (
                    testSuite.name
                  )}
                </TableCell>
                <TableCell>
                  {editMode === testSuite._id ? (
                    <TextField
                      value={testSuiteVersionEditedRow.description || ""}
                      onChange={(e) => handleInputChange(testSuite.versionNumber, "description", e.target.value)}
                    />
                  ) : (
                    testSuite.description
                  )}
                </TableCell>
                <TableCell>{testSuite.testCases?.length || 0}</TableCell>
                <TableCell sx={{display: "flex", justifyContent: "flex-end"}}>
                  {editMode === testSuite._id ? (
                    <>
                      <IconButton onClick={()=>handleUpdateTestSuiteVersion(testSuite._id)}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelEdit}>
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <>
                    {testSuite.status !== "Published" && (
                     <IconButton onClick={() => handleTestSuiteVersionEdit(testSuite._id)}>
                    <Edit />
                  </IconButton>
                    )}
                  {testSuite.status === "Published" && (
                    <Button variant="contained" color="primary" onClick={handleAddNewTestSuiteVersionRow}>
                      Add New Version
                    </Button>
                  )}
                  {testSuite.status !== "Published" && (
                  <IconButton onClick={()=>handlePublishTestSuiteVersion(testSuite._id)}>
                    <Publish />
                  </IconButton>
                  )}
                  <IconButton onClick={() => navigate(`/tenant/${tenantId}/testSuite/${testSuite._id}/TestCases`)}>
                    <Visibility />
                  </IconButton>
                  <IconButton>
                    <PlayArrow />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteTestSuiteVersion(testSuite._id)}>
                    <Delete />
                  </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
             {newTestSuiteVersionRow && (
              <TableRow>
                <TableCell>{newTestSuiteVersionRow.versionNumber}</TableCell>
                <TableCell>
                  <TextField
                    value={newTestSuiteVersionRow.name}
                    onChange={(e) => handleInputChange(-1, "name", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={newTestSuiteVersionRow.description}
                    onChange={(e) => handleInputChange(-1, "description", e.target.value)}
                  />
                </TableCell>
                <TableCell>{"0"}</TableCell>
                <TableCell>
                <TableCell>
                  <IconButton onClick={handleCreateNewTestSuiteVersion}>
                    <Save />
                  </IconButton>
                  <IconButton onClick={handleCancelEdit}>
                    <Cancel />
                  </IconButton>
                </TableCell>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
